import { ObjectGraph } from '@jet/environment/dependencies';
import type { UrlRuleDefinition } from '@jet/environment/routing';
import { URL } from '@jet/environment/util/urls';
import { APP_QUERY_PARAMS } from '../constants';
import { normalizeLocale } from '../dependencies/locale';
import type { RouteableIntent } from '../intents';

/**
 * Pattern to replace substrings like `{storefront}` in a url rule path.
 */
const placeholderPattern = /\{(\w+)\}/g;

/**
 * Construct a canonical url (i.e. has a storefront) to decide if we should do a
 * server-side redirect _before_ hitting UTS to determine the same thing. Note:
 * we cannot do this for routes with `{seo}`, or any data that can only be known
 * from a UTS response.
 *
 * Use in `staticCanonicalUrlFor()` functions, and/or if the url we want to show
 * is different than the one in the UTS response data (i.e. the UTS url for
 * `/watch-now` is <https://classical.music.apple.com/us/root/watch-now/tahoma_watchnow>).
 * @see <https://github.pie.apple.com/music/music-ui-js/blob/2022/main/src/utilities/generateRoutes.ts>
 */
function makeCanonicalUrl<I extends RouteableIntent>(
    { path, query }: UrlRuleDefinition,
    intent: I,
    objectGraph: ObjectGraph,
): string | never {
    if (
        typeof path !== 'string' ||
        !path.startsWith('/') ||
        path.includes('{seo}')
    ) {
        throw new Error(`Invalid path pattern: "${path}"`);
    }

    // Render path with storefront and request ids.
    const renderedPath = path.replace(
        placeholderPattern,
        (original, placeholder) =>
            intent[placeholder] != null ? intent[placeholder] : original,
    );

    // Apply query params. Note: the origin is ignored in url checks.
    return updateCanonicalUrl(
        `https://classical.music.apple.com${renderedPath}`,
        intent,
        query,
        objectGraph,
    );
}

/**
 * Update a canonical url to add query params from the intent data. Use to
 * persist any query params in a UTS response url in a `perform()` function.
 */
function updateCanonicalUrl(
    canonicalUrl: string,
    intent: RouteableIntent,
    query: string[] | undefined,
    objectGraph: ObjectGraph,
): string {
    const { storefront, language } = normalizeLocale(intent, objectGraph);
    const data = { ...intent, l: language, storefront };

    // Apply query params.
    const url = new URL(canonicalUrl);
    getValidQueryParams(query).forEach(
        (key) => data[key] != null && url.param(key, data[key]),
    );

    return url.toString();
}

/**
 * Construct a sorted array of valid query params. Query rule params that are
 * optional have a trailing `?`, which needs to be removed. Note: affiliate and
 * marketing query params are handled in `media-apps/apps/music-classical/src/server.ts`;
 */
function getValidQueryParams(ruleQuery: string[] = []): string[] {
    const params = ruleQuery
        .map((key) => key.replace('?', ''))
        .concat(APP_QUERY_PARAMS);
    params.sort();
    return params;
}

export { makeCanonicalUrl, updateCanonicalUrl };
